import { Star, Crown, Check, X } from "lucide-react";
import { PhoneMock } from "../PhoneMock";

/**
 * App preview: the Post Match flow.
 *
 * Blocking modal that appears 30 minutes after tip off: confirm
 * attendance, rate the court, and vote for Player of the Match. This is
 * where Whistll's retention loop lives - previously not surfaced on the
 * marketing site at all. Distinct from the Match Room, which is the
 * pre/during-game chat area.
 */
export function PostMatchMock({ className }: { className?: string }) {
  return (
    <PhoneMock className={className}>
      {/* Status bar */}
      <div className="flex items-center justify-between px-6 pt-4 pb-1 text-[10px] font-semibold">
        <span>9:41</span>
        <span className="flex items-center gap-1 text-[9px]">● ● ●</span>
      </div>

      {/* Dim backdrop + modal */}
      <div className="relative h-full w-full px-3 pt-3 pb-6">
        {/* Faint behind-modal context */}
        <div className="absolute inset-0 bg-[#0d1117]" />
        <div
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-1/3 h-[220px] w-[220px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-30 blur-3xl"
          style={{
            backgroundImage:
              "radial-gradient(closest-side, rgba(255,98,0,0.4), transparent 70%)",
          }}
        />

        {/* Modal card */}
        <div className="relative mt-2 rounded-2xl border border-[#1e2832] bg-[#131a20] p-3.5 shadow-2xl">
          {/* Header */}
          <div className="mb-2.5 flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[9px] font-bold uppercase tracking-wider text-[#FF6200]">
                Match Room
              </p>
              <p className="mt-0.5 truncate text-[12px] font-black text-[#F5F5DC]">
                5v5 · Brixton Rec
              </p>
              <p className="text-[9px] text-[#8899A6]">Final · 19:00 tip off</p>
            </div>
            <div className="shrink-0 rounded-full bg-[#FF6200]/15 px-2 py-1 text-[8px] font-black text-[#FF6200]">
              W 42–38
            </div>
          </div>

          {/* Attendance question */}
          <p className="mb-1.5 text-[10px] font-bold text-[#F5F5DC]">
            Did you play this game?
          </p>
          <div className="mb-3 grid grid-cols-2 gap-1.5">
            <button className="flex items-center justify-center gap-1 rounded-lg bg-[#FF6200] py-1.5 text-[10px] font-black text-[#0d1117]">
              <Check className="h-3 w-3" strokeWidth={3} />
              Yes
            </button>
            <button className="flex items-center justify-center gap-1 rounded-lg border border-[#1e2832] bg-[#0d1117] py-1.5 text-[10px] font-bold text-[#8899A6]">
              <X className="h-3 w-3" />
              No
            </button>
          </div>

          {/* Court rating */}
          <p className="mb-1 text-[10px] font-bold text-[#F5F5DC]">
            Rate the court
          </p>
          <div className="mb-3 flex items-center gap-1.5 rounded-lg bg-[#0d1117] p-2">
            <div className="flex gap-0.5">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star
                  key={i}
                  className={`h-3.5 w-3.5 ${
                    i <= 4 ? "text-[#FF6200]" : "text-[#4a5568]"
                  }`}
                  fill={i <= 4 ? "#FF6200" : "none"}
                  strokeWidth={2}
                />
              ))}
            </div>
            <span className="ml-auto text-[9px] font-bold text-[#F5F5DC]">
              4.0
            </span>
          </div>

          {/* POTM vote */}
          <p className="mb-1.5 flex items-center gap-1 text-[10px] font-bold text-[#F5F5DC]">
            <Crown className="h-3 w-3 text-[#FF6200]" />
            Player of the Match
          </p>
          <div className="flex flex-col gap-1">
            <PlayerVoteRow initials="DK" name="Darren K." selected />
            <PlayerVoteRow initials="MP" name="Marcus P." />
            <PlayerVoteRow initials="AO" name="Ade O." />
          </div>

          {/* Footer hint */}
          <p className="mt-3 text-center text-[8px] text-[#4a5568]">
            Majority vote locks the result. No-shows update automatically.
          </p>
        </div>
      </div>
    </PhoneMock>
  );
}

function PlayerVoteRow({
  initials,
  name,
  selected = false,
}: {
  initials: string;
  name: string;
  selected?: boolean;
}) {
  return (
    <button
      className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors"
      style={{
        background: selected ? "rgba(255,98,0,0.12)" : "#0d1117",
        border: selected ? "1px solid #FF6200" : "1px solid #1e2832",
      }}
    >
      <div
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[9px] font-black"
        style={{
          background: selected ? "#FF6200" : "#1e2832",
          color: selected ? "#0d1117" : "#8899A6",
        }}
      >
        {initials}
      </div>
      <span className="flex-1 text-[10px] font-semibold text-[#F5F5DC]">
        {name}
      </span>
      {selected && (
        <Check className="h-3 w-3 text-[#FF6200]" strokeWidth={3} />
      )}
    </button>
  );
}
